import React, { useEffect, useMemo } from "react";
import Map from "@/components/Map";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Clock, Phone, Mail } from "lucide-react";

const ContactSchema = z.object({
  nom: z.string().min(1, "Nom requis"),
  email: z.string().email("Email invalide"),
  phone: z.string().min(1, "Téléphone requis"),
  objet: z.string().min(1, "Objet requis"),
  message: z.string().min(1, "Message requis"),
});

type ContactFormValues = z.infer<typeof ContactSchema>;

const Contact: React.FC = () => {
  const { toast } = useToast();
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(ContactSchema),
    defaultValues: { nom: "", email: "", phone: "", objet: "", message: "" },
    mode: "onTouched",
  });


  useEffect(() => {
    const title = "Contact | Mon Copieur";
    document.title = title;
    const desc = "Contact Mon Copieur — adresse, téléphone, e‑mail et formulaire de contact.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = desc;
  }, []);


  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact Mon Copieur",
    url: "/contact",
    publisher: {
      "@type": "Organization",
      name: "Mon Copieur",
      logo: "/lovable-uploads/d5b011bc-34ae-449a-8ec6-8e13ea03ceae.png",
    },
  }), []);

  const onSubmit = async (values: ContactFormValues) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const payload = {
        contact_name: values.nom,
        email: values.email,
        phone: values.phone,
        subject: values.objet,
        message: values.message,
        tags: "Contact",
      };

      const res = await fetch("https://moncopieur.cloud-erp.ma/ws/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const contentType = res.headers.get("content-type") || "";
      const data: any = contentType.includes("application/json") ? await res.json() : await res.text();
      const success = typeof data === "object" && (data?.success === true || data?.success === "True" || data?.success === 1);

      if (success) {
        toast({
          title: "Message envoyé avec succès",
          variant: "success",
        });
        form.reset();
      } else {
        throw new Error((typeof data === "object" && data?.message) || "Échec de l'envoi");
      }
    } catch (error: any) {
      toast({
        title: "Une erreur est survenue",
        description: error?.name === "AbortError" ? "Délai dépassé. Veuillez réessayer plus tard." : "Impossible d'envoyer votre message. Veuillez réessayer plus tard.",
        variant: "destructive",
      });
      // eslint-disable-next-line no-console
      console.error("Contact form error:", error);
    } finally {
      clearTimeout(timeoutId);
    }
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <main>
        <header className="border-b border-border bg-secondary/40">
          <div className="container mx-auto px-4 py-10">
            <h1 className="text-3xl font-semibold">Contact – Mon Copieur</h1>
            <p className="mt-2 text-muted-foreground">Retrouvez notre adresse, nos coordonnées et envoyez-nous un message.</p>
          </div>
        </header>

        <section aria-label="Localisation" className="container mx-auto px-4 py-10">
          <div className="mb-4 flex items-center justify-start gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>Notre boutique (Casablanca, MA)</span>
            </div>
          </div>
          <Map center={[-7.592245420240844, 33.589346980832254]} />
        </section>

        <section aria-label="Coordonnées" className="border-y border-border bg-secondary/40">
          <div className="container mx-auto grid gap-6 px-4 py-10 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> Adresse & horaires</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>194 Boulevard Ba Hmad</p>
                <p>Casablanca 20300, Maroc</p>
                <div className="mt-4 flex items-center gap-2 text-foreground"><Clock className="h-4 w-4" /><span>Horaires</span></div>
                <ul className="mt-2 space-y-1">
                  <li>Lun–Ven:  9:00-12:30 14:30 - 19:00</li>
                  <li>Sam: 9:00 – 13:00</li>
                  <li>Dim: Fermé</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Phone className="h-5 w-5 text-primary" /> Téléphone</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <ul className="space-y-2">
                  <li className="flex items-center justify-between text-muted-foreground"><span>Commercial</span><a href="tel:+212521818181" className="font-medium text-foreground hover:underline">+212 5 21 81 81 81</a></li>
                  <li className="flex items-center justify-between text-muted-foreground"><span>Facturation</span><a href="tel:+212521717171" className="font-medium text-foreground hover:underline">+212 5 21 71 71 71</a></li>
                  <li className="flex items-center justify-between text-muted-foreground"><span>SAV</span><a href="tel:+212521828282" className="font-medium text-foreground hover:underline">+212 5 21 82 82 82</a></li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> E‑mail</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <ul className="space-y-2">
                  <li className="flex items-center justify-between text-muted-foreground"><span>Commercial</span><a href="mailto:commercial@moncopieur.ma" className="font-medium text-foreground hover:underline">commercial@moncopieur.ma</a></li>
                  <li className="flex items-center justify-between text-muted-foreground"><span>Facturation</span><a href="mailto:facturation@moncopieur.ma" className="font-medium text-foreground hover:underline">facturation@moncopieur.ma</a></li>
                  <li className="flex items-center justify-between text-muted-foreground"><span>SAV</span><a href="mailto:sav@moncopieur.ma" className="font-medium text-foreground hover:underline">sav@moncopieur.ma</a></li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section aria-label="Formulaire de contact" className="container mx-auto px-4 py-12">
          <div className="mx-auto max-w-3xl">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="nom"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nom</FormLabel>
                        <FormControl>
                          <Input placeholder="Votre nom complet" {...field} required />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="vous@exemple.com" {...field} required />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Téléphone</FormLabel>
                        <FormControl>
                          <Input placeholder="+212 ..." {...field} required />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="objet"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Objet</FormLabel>
                        <FormControl>
                          <Input placeholder="Sujet de votre message" {...field} required />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea rows={6} placeholder="Votre message..." {...field} required />
                      </FormControl>
                      <FormDescription>Tous les champs sont obligatoires.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end">
                  <Button type="submit" size="lg" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? "Envoi..." : "Envoyer"}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </section>
      </main>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </div>
  );
};

export default Contact;
